import { useRef, useState } from 'react';
import type { InputHTMLAttributes, KeyboardEvent } from 'react';
import { excedeElCampo, PAUSA_MS, sumarTecla, terminaEnLectura, textoDe, type Tecla } from '../../lib/lector';
import { filtrarNumero, interpretarNumero, separarMiles, textoDeNumero } from './numero';

/**
 * Una ráfaga del lector no se queda en el campo: el valor previo a ella se restaura y el código va a
 * `alLeer` (`terminado` = ya llegó su Enter). Sin `alLeer`, se descarta.
 */
export interface OpcionLector {
  maxDigitos: number;
  alLeer?: (codigo: string, terminado: boolean) => void;
}

/**
 * Campo numérico de `NumeroEnLinea` y `CampoNumero`: admite quedar vacío, selecciona todo al enfocar, es
 * `type="text"` con teclado numérico y reporta a cada tecla, para que los totales se muevan al escribir.
 */
export function usarNumero({
  valor,
  onValor,
  min,
  max,
  decimales = false,
  plata = false,
  lector,
}: {
  valor: number | string | null | undefined;
  /** `null` es campo vacío, que no siempre significa cero: lo decide quien recibe. */
  onValor: (n: number | null) => void;
  min?: number;
  max?: number;
  decimales?: boolean;
  plata?: boolean;
  lector?: OpcionLector;
}): InputHTMLAttributes<HTMLInputElement> {
  const [texto, setTexto] = useState<string | null>(null);
  const mostrar = (t: string) => (plata ? separarMiles(t) : t);
  const rafaga = useRef<Tecla[]>([]);
  const valorAntes = useRef<number | null>(null);
  /** Detectada una lectura sin destino, el resto de la ráfaga se traga hasta la próxima pausa. */
  const descartando = useRef(false);

  const numeroDe = (v: typeof valor) =>
    typeof v === 'number' ? v : interpretarNumero(String(v ?? ''), { min, max, decimales });
  const restaurar = () => {
    setTexto(null);
    onValor(valorAntes.current);
  };
  const reiniciar = () => {
    rafaga.current = [];
    descartando.current = false;
  };

  function alTeclear(e: KeyboardEvent<HTMLInputElement>) {
    if (!lector || e.nativeEvent.isComposing || e.ctrlKey || e.metaKey || e.altKey) return;
    const ultima = rafaga.current[rafaga.current.length - 1];
    if (descartando.current && ultima && e.timeStamp - ultima.t >= PAUSA_MS) reiniciar();

    if (e.key === 'Enter') {
      if (descartando.current) {
        e.preventDefault();
      } else if (terminaEnLectura(rafaga.current, e.timeStamp, { campoNumerico: true })) {
        e.preventDefault();
        restaurar();
        if (lector.alLeer) lector.alLeer(textoDe(rafaga.current), true);
      }
      reiniciar();
      return;
    }
    if (e.repeat) return;
    if (e.key.length !== 1) {
      reiniciar();
      return;
    }

    const nueva = sumarTecla(rafaga.current, { tecla: e.key, t: e.timeStamp });
    if (nueva.length === 1) valorAntes.current = numeroDe(valor);
    rafaga.current = nueva;
    if (descartando.current) {
      e.preventDefault();
      return;
    }
    if (excedeElCampo(nueva, lector.maxDigitos)) {
      e.preventDefault();
      restaurar();
      if (lector.alLeer) {
        lector.alLeer(textoDe(nueva), false);
        reiniciar();
      } else {
        descartando.current = true;
      }
    }
  }

  return {
    type: 'text',
    inputMode: decimales ? 'decimal' : 'numeric',
    value: texto ?? mostrar(textoDeNumero(valor)),
    // Al enfocar y **también** al hacer clic: `onFocus` no vuelve a disparar si el campo ya tenía el
    // foco, y entonces el segundo clic dejaba la tecla pegada al valor anterior.
    onFocus: (e) => {
      reiniciar();
      e.currentTarget.select();
    },
    onClick: (e) => {
      reiniciar();
      e.currentTarget.select();
    },
    onKeyDown: alTeclear,
    onChange: (e) => {
      const limpio = filtrarNumero(e.target.value, decimales);
      setTexto(mostrar(limpio));
      onValor(interpretarNumero(limpio, { min, max, decimales }));
    },
    onBlur: () => setTexto(null),
  };
}
